import { useRef } from 'react';

import { ukLocale } from '@core/Utils/Intl/MappedLocales';
import Button from '@gds/Button/Button';
import { Input } from '@gds/Input/Input';
import Slider from '@gds/Slider/Slider';
import { Stepper } from '@gds/Stepper/Stepper';
import { Typography } from '@gds/Typography/Typography';
import { useRangeFilters } from '@search-filters/Hooks/UseRangeFilters';

import type { FilterControlBaseProps } from '../../FilterPanel.entity';
import { ENGINE_POWER_RANGE } from './PowerFilter.constants';

import piStyles from '../PairedInputs/PairedInputs.module.css';
import styles from './PowerFilter.module.css';

type PowerFilterProps = Pick<FilterControlBaseProps, 'strings' | 'tenant'>;

export const PowerFilter = ({ strings, tenant }: PowerFilterProps) => {
  const minVal = ENGINE_POWER_RANGE[0].toString();
  const maxVal = ENGINE_POWER_RANGE[ENGINE_POWER_RANGE.length - 1].toString();

  const {
    gteInputProps,
    lteInputProps,
    sliderProps,
    canReset,
    reset,
    totalCount,
    stepGte,
    stepLte,
  } = useRangeFilters('engine-power', ENGINE_POWER_RANGE, [minVal, maxVal], undefined);

  const gteInputRef = useRef<HTMLInputElement>(null);
  const lteInputRef = useRef<HTMLInputElement>(null);

  return (
    <section className={styles.powerFilter}>
      <div className={piStyles.pairedInputs}>
        <Input
          {...gteInputProps}
          ref={gteInputRef}
          label={strings.min}
          type="number"
          min={0}
          name="min-power"
          rightAddons={
            <Stepper
              label="minimum power"
              focusTargetRef={gteInputRef}
              onIncrement={() => stepGte(1)}
              onDecrement={() => stepGte(-1)}
            />
          }
          className={styles.rangeInput}
          rightAddonsClassName={styles.stepperWrapper}
          wrapperClassName={styles.rangeInputControl}
          labelPlacement={tenant === 'rac' ? 'floating' : 'above'}
        />
        <Input
          {...lteInputProps}
          ref={lteInputRef}
          label={strings.max}
          type="number"
          max={maxVal}
          name="max-power"
          rightAddons={
            <Stepper
              label="maximum power"
              focusTargetRef={lteInputRef}
              onIncrement={() => stepLte(1)}
              onDecrement={() => stepLte(-1)}
            />
          }
          className={styles.rangeInput}
          rightAddonsClassName={styles.stepperWrapper}
          wrapperClassName={styles.rangeInputControl}
          labelPlacement={tenant === 'rac' ? 'floating' : 'above'}
        />
      </div>
      <div className={styles.auxInfo}>
        <Typography Component="span" className={styles.resultCount} variant="caption2">
          ({totalCount.toLocaleString(ukLocale)})
        </Typography>
        <Button
          color="secondary"
          name="reset"
          disabled={!canReset()}
          onClick={() => reset()}
          size="small"
          variant="link"
        >
          {strings.reset}
        </Button>
      </div>
      <Slider {...sliderProps} dataTestId="power-slider" />
    </section>
  );
};
